// client/src/utils/VipBuyerContext.jsx
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from "react";
import { useAuth } from "@/components/hooks/useAuth";
import { getBuyerByAuth0Id } from "@/utils/api";
import { getLogger } from "@/utils/logger";

const log = getLogger("VipBuyerContext");
const RETRYABLE_STATUS_CODES = new Set([429, 500, 502, 503, 504]);
const VIP_LOOKUP_MAX_RETRIES = 1;
const VIP_LOOKUP_RETRY_DELAY_MS = 250;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Create a context for VIP buyer information
export const VipBuyerContext = createContext(null);

export const VipBuyerProvider = ({ children }) => {
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    auth0User,
    authReadyForProtectedRoutes,
  } = useAuth();
  const [isVipBuyer, setIsVipBuyer] = useState(false);
  const [vipBuyerData, setVipBuyerData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState(null);
  const activeRequestRef = useRef(null);
  const resolvedAuth0IdRef = useRef(null);

  const resetVipState = useCallback(() => {
    setIsVipBuyer(false);
    setVipBuyerData(null);
    setError(null);
    setIsLoading(false);
    setChecked(true);
    resolvedAuth0IdRef.current = null;
    activeRequestRef.current = null;
  }, []);

  const fetchVipBuyerStatus = useCallback(
    async (options = {}) => {
      const { force = false } = options;
      const auth0Id = auth0User?.sub || null;

      if (!isAuthenticated || !auth0Id) {
        resetVipState();
        return null;
      }

      if (isAuthLoading || !authReadyForProtectedRoutes) {
        setIsLoading(true);
        setChecked(false);
        return null;
      }

      if (!force && resolvedAuth0IdRef.current === auth0Id) {
        return vipBuyerData;
      }

      if (!force && activeRequestRef.current) {
        return activeRequestRef.current;
      }

      const request = (async () => {
        setIsLoading(true);
        setChecked(false);
        setError(null);

        for (let attempt = 0; attempt <= VIP_LOOKUP_MAX_RETRIES; attempt += 1) {
          try {
            const buyerData = await getBuyerByAuth0Id(auth0Id);
            // isVip is computed by the backend (source === "VIP Buyers List")
            setIsVipBuyer(buyerData?.isVip === true);
            setVipBuyerData(buyerData || null);
            setError(null);
            setChecked(true);
            resolvedAuth0IdRef.current = auth0Id;
            return buyerData || null;
          } catch (err) {
            const status = err?.response?.status;

            if (status === 404) {
              log.error(
                `[VipBuyerContext:fetchVipBuyerStatus] > [Request]: getBuyerByAuth0Id > [Response]: no buyer found for authenticated user — auth0Id not linked to any buyer record > [Comment]: auth0Id=${auth0Id}`
              );
              setIsVipBuyer(false);
              setVipBuyerData(null);
              setError(null);
              setChecked(true);
              resolvedAuth0IdRef.current = auth0Id;
              return null;
            }

            const shouldRetry =
              attempt < VIP_LOOKUP_MAX_RETRIES && RETRYABLE_STATUS_CODES.has(status);

            if (shouldRetry) {
              await delay(VIP_LOOKUP_RETRY_DELAY_MS * (attempt + 1));
              continue;
            }

            log.error(
              `[VipBuyerContext:fetchVipBuyerStatus] > [Request]: getBuyerByAuth0Id > [Response]: error=${err?.message || "unknown"} > [Comment]: auth0Id=${auth0Id}`,
              { status }
            );
            setIsVipBuyer(false);
            setVipBuyerData(null);
            setError("Failed to verify VIP status");
            setChecked(true);
            resolvedAuth0IdRef.current = null;
            return null;
          }
        }

        return null;
      })();

      activeRequestRef.current = request;

      try {
        return await request;
      } finally {
        if (activeRequestRef.current === request) {
          activeRequestRef.current = null;
        }
        setIsLoading(false);
      }
    },
    [
      authReadyForProtectedRoutes,
      auth0User?.sub,
      isAuthenticated,
      isAuthLoading,
      resetVipState,
      vipBuyerData,
    ]
  );

  useEffect(() => {
    if (!isAuthenticated || !auth0User?.sub) {
      resetVipState();
      return;
    }

    if (isAuthLoading || !authReadyForProtectedRoutes) {
      setIsLoading(true);
      setChecked(false);
      return;
    }

    void fetchVipBuyerStatus();
  }, [
    authReadyForProtectedRoutes,
    auth0User?.sub,
    fetchVipBuyerStatus,
    isAuthenticated,
    isAuthLoading,
    resetVipState,
  ]);

  const refreshVipStatus = useCallback(async () => {
    return fetchVipBuyerStatus({ force: true });
  }, [fetchVipBuyerStatus]);

  // The context value that will be provided
  const value = {
    isVipBuyer,
    vipBuyerData,
    isLoading,
    checked,
    error,
    refreshVipStatus,
  };

  return (
    <VipBuyerContext.Provider value={value}>
      {children}
    </VipBuyerContext.Provider>
  );
};

// Custom hook for using the VIP buyer context
export const useVipBuyer = () => {
  const context = useContext(VipBuyerContext);
  if (context === null) {
    throw new Error("useVipBuyer must be used within a VipBuyerProvider");
  }
  return context;
};
